import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiService } from '../services/api';
import { Question } from '../types';
import TimerWrapper from '../components/TimerWrapper';
import { StudentSessionStats } from '../components/SessionStatistics';
import { marked } from 'marked';

const StudentAutonomousQuiz: React.FC = () => {
  const { sessionCode } = useParams<{ sessionCode: string }>();
  const [searchParams] = useSearchParams();
  const studentName = searchParams.get('name');
  const navigate = useNavigate();

  const [session, setSession] = useState<any>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [answerSubmitted, setAnswerSubmitted] = useState<boolean>(false);
  const [answeredQuestions, setAnsweredQuestions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timerExpired, setTimerExpired] = useState<boolean>(false);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [sessionLoadedAfterCompletion, setSessionLoadedAfterCompletion] = useState(false);

  // Fonction pour afficher le type de question
  const getQuestionTypeDisplay = (type: string) => {
    switch (type) {
      case 'multiple_choice':
        return '📝 Choix multiple';
      case 'investigation':
        return '🕵️ Investigation';
      case 'true_false':
        return '✅ Vrai/Faux';
      case 'open_ended':
        return '✍️ Question ouverte';
      case 'rating':
        return '⭐ Évaluation';
      default:
        return '❓ Question';
    }
  };

  // État pour stocker le score calculé
  const [calculatedScore, setCalculatedScore] = useState<number>(0);
  const [scoreCalculated, setScoreCalculated] = useState<boolean>(false);

  // Récupérer le nom depuis le localStorage s'il existe
  const getStoredStudentName = useCallback(() => {
    try {
      const stored = localStorage.getItem(`student_name_${sessionCode}`);
      
      return studentName || stored || '';
    } catch (e) {
      return studentName || '';
    }
  }, [sessionCode, studentName]);

  const [showNameModal, setShowNameModal] = useState(!getStoredStudentName());
  const [inputName, setInputName] = useState('');
  const [finalStudentName, setFinalStudentName] = useState(getStoredStudentName());

  const currentQuestion = questions[currentQuestionIndex];
  const studentId = `student_${sessionCode}_${finalStudentName?.toLowerCase().replace(/\s+/g, '_') || 'anonymous'}`;

  // Fonction pour calculer le score réel basé sur les réponses de l'étudiant
  const calculateRealScore = useCallback(async () => {
    if (!questions.length || !sessionCode || !finalStudentName) return 0;
    
    try {
      const studentId = `student_${sessionCode}_${finalStudentName.toLowerCase().replace(/\s+/g, '_')}`;
      
      // Filtrer les questions qui ont une bonne réponse définie (QCM et Vrai/Faux)
      const questionsWithCorrectAnswer = questions.filter(question => 
        question.correctAnswer !== undefined && 
        question.correctAnswer !== null && 
        question.correctAnswer !== '' &&
        (question.type === 'multiple_choice' || question.type === 'true_false')
      );
      
      // Filtrer les questions ouvertes
      const openEndedQuestions = questions.filter(question => 
        question.type === 'open_ended'
      );
      
      // Récupérer les réponses de l'étudiant pour les questions QCM/VF
      let correctAnswers = 0;
      
      for (const question of questionsWithCorrectAnswer) {
        try {
          const response = await ApiService.getQuestion(question.id);
          if (response.success && response.question.responses) {
            const studentResponse = response.question.responses.find((r: any) => r.studentId === studentId);
            if (studentResponse) {
              // Convertir la réponse de l'étudiant en index
              const studentAnswerIndex = parseInt(studentResponse.answer);
              const correctAnswerIndex = parseInt(question.correctAnswer || '');
              
              if (!isNaN(studentAnswerIndex) && !isNaN(correctAnswerIndex) && studentAnswerIndex === correctAnswerIndex) {
                correctAnswers++;
              }
            }
          }
        } catch (error) {
          console.error(`Erreur récupération question ${question.id}:`, error);
        }
      }
      
      // Récupérer les scores de concordance pour les questions ouvertes
      let openEndedScore = 0;
      let evaluatedOpenEndedCount = 0;
      
      for (const question of openEndedQuestions) {
        try {
          const response = await ApiService.getQuestion(question.id);
          if (response.success && response.question.responses) {
            const studentResponse = response.question.responses.find((r: any) => r.studentId === studentId);
            if (studentResponse && studentResponse.concordanceScore !== null && studentResponse.concordanceScore !== undefined) {
              openEndedScore += studentResponse.concordanceScore;
              evaluatedOpenEndedCount++;
            }
          }
        } catch (error) {
          console.error(`Erreur récupération question ouverte ${question.id}:`, error);
        }
      }
      
      // Calculer la précision globale (QCM/VF + Questions ouvertes) - même logique que SessionStatistics
      const totalEvaluableQuestions = questionsWithCorrectAnswer.length + evaluatedOpenEndedCount;
      
      if (totalEvaluableQuestions === 0) return 0;
      
      // Score des QCM/VF (0 ou 100%)
      const qcmScore = questionsWithCorrectAnswer.length > 0 ? (correctAnswers / questionsWithCorrectAnswer.length) * 100 : 0;
      
      // Score moyen des questions ouvertes
      const openEndedAverageScore = evaluatedOpenEndedCount > 0 ? openEndedScore / evaluatedOpenEndedCount : 0;
      
      // Calculer la moyenne pondérée
      const qcmWeight = questionsWithCorrectAnswer.length / totalEvaluableQuestions;
      const openEndedWeight = evaluatedOpenEndedCount / totalEvaluableQuestions;
      
      const globalAccuracyRate = (qcmScore * qcmWeight) + (openEndedAverageScore * openEndedWeight);
      
      return Math.round(globalAccuracyRate);
      
    } catch (error) {
      console.error('Erreur calcul score:', error);
      return 0;
    }
  }, [questions, sessionCode, finalStudentName]);

  // Calculer le score quand le message de completion s'affiche
  useEffect(() => {
    if (showCompletionMessage && !scoreCalculated) {
      calculateRealScore().then(score => {
        setCalculatedScore(score);
        setScoreCalculated(true);
      });
    }
  }, [showCompletionMessage, scoreCalculated, calculateRealScore]);

  // Fonction pour obtenir le score (utilise le score calculé ou 0 par défaut)
  const getCurrentScore = () => {
    return scoreCalculated ? calculatedScore : 0;
  };

  // Fonction pour charger les questions déjà répondues depuis la base de données
  const loadAnsweredQuestionsFromDB = useCallback(async (studentId: string, questionsList: Question[]) => {
    const answeredSet = new Set<string>();
    console.log('🔍 Chargement des questions répondues pour:', studentId);
    
    // Utiliser Promise.all pour paralléliser les appels
    const promises = questionsList.map(async (question) => {
      try {
        const response = await ApiService.getQuestion(question.id);
        if (response.success && response.question.responses) {
          const hasAnswered = response.question.responses.some((response: any) => response.studentId === studentId);
          if (hasAnswered) {
            console.log('✅ Question répondue trouvée:', question.id);
            return question.id;
          }
        }
        return null;
      } catch (error) {
        console.error(`Erreur vérification question ${question.id}:`, error);
        return null;
      }
    });
    
    try {
      const results = await Promise.all(promises);
      results.forEach(questionId => {
        if (questionId) {
          answeredSet.add(questionId);
        }
      });
      
      console.log('📊 Questions répondues trouvées:', Array.from(answeredSet));
      return answeredSet;
    } catch (error) {
      console.error('Erreur chargement réponses:', error);
      return new Set<string>();
    }
  }, []);

  // Fonction pour trouver la prochaine question non répondue à partir des données de la DB
  const findNextUnansweredQuestionFromDB = useCallback((answeredSet: Set<string>, questionsList: Question[]) => {
    for (let i = 0; i < questionsList.length; i++) {
      if (!answeredSet.has(questionsList[i].id)) {
        return i;
      }
    }
    return -1; // Toutes les questions ont été répondues
  }, []);

  const connectToSession = useCallback(async (studentName: string) => {
    try {
      const studentId = `student_${sessionCode}_${studentName.toLowerCase().replace(/\s+/g, '_')}`;
      const response = await ApiService.joinSession(
        sessionCode?.toUpperCase() || '',
        studentId,
        studentName
      );
      
      if (response.success) {
        console.log(`✅ Étudiant connecté: ${studentName}`);
        // Sauvegarder aussi le nom ici au cas où
        try {
          localStorage.setItem(`student_name_${sessionCode}`, studentName);
        } catch (e) {
          // Ignorer les erreurs de localStorage
        }
      }
    } catch (err) {
      console.error('Erreur lors de la connexion à la session:', err);
      // Ne pas bloquer le flux si la connexion échoue
    }
  }, [sessionCode]);

  const loadSession = useCallback(async () => {
    if (!sessionCode) {
      setError('Code de session manquant');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Récupérer la session par code
      const response = await ApiService.getSessionByCode(sessionCode.toUpperCase());
      
      if (response.success) {
        setSession(response.session);
        setQuestions(response.session.questions || []);
        
        // Si le nom est déjà stocké, utiliser celui-là
        const storedName = getStoredStudentName();
        if (storedName && storedName !== finalStudentName) {
          setFinalStudentName(storedName);
          // Connecter automatiquement
          await connectToSession(storedName);
        }
        
        // Charger les questions déjà répondues depuis la base de données
        const currentStudentId = `student_${sessionCode}_${(storedName || finalStudentName)?.toLowerCase().replace(/\s+/g, '_') || 'anonymous'}`;
        
        // Essayer de charger depuis la DB, avec fallback localStorage
        let answeredQuestionsFromDB = new Set<string>();
        try {
          answeredQuestionsFromDB = await loadAnsweredQuestionsFromDB(currentStudentId, response.session.questions || []);
        } catch (error) {
          console.warn('⚠️ Erreur chargement DB, utilisation localStorage:', error);
          // Fallback: utiliser localStorage
          const storedAnswered = localStorage.getItem(`answered_${sessionCode}_${currentStudentId}`);
          if (storedAnswered) {
            answeredQuestionsFromDB = new Set(JSON.parse(storedAnswered));
            console.log('📦 Questions répondues depuis localStorage:', Array.from(answeredQuestionsFromDB));
          }
        }
        
        setAnsweredQuestions(answeredQuestionsFromDB);
        
        // Trouver la prochaine question non répondue
        const nextUnansweredIndex = findNextUnansweredQuestionFromDB(answeredQuestionsFromDB, response.session.questions || []);
        console.log('🎯 Index de la prochaine question non répondue:', nextUnansweredIndex);
        console.log('📋 Questions disponibles:', response.session.questions?.length || 0);
        console.log('📊 Questions répondues:', answeredQuestionsFromDB.size);
        
        if (nextUnansweredIndex !== -1) {
          setCurrentQuestionIndex(nextUnansweredIndex);
          console.log('✅ Navigation vers la question:', nextUnansweredIndex);
        } else {
          console.log('🏁 Toutes les questions ont été répondues');
          // Si toutes les questions sont répondues, afficher le message de completion
          setShowCompletionMessage(true);
          setSessionLoadedAfterCompletion(true);
          console.log('🎉 Affichage du message de completion');
        }
      } else {
        setError('Session non trouvée');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement de la session');
    } finally {
      setLoading(false);
    }
  }, [sessionCode, finalStudentName, getStoredStudentName, connectToSession, loadAnsweredQuestionsFromDB, findNextUnansweredQuestionFromDB]);

  
  useEffect(() => {

    loadSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadSession]);

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputName.trim()) {
      setError('Veuillez entrer votre nom');
      return;
    }
    const name = inputName.trim();
    setFinalStudentName(name);
    setShowNameModal(false);
    setError(null);
    
    // Sauvegarder le nom dans le localStorage
    try {
      localStorage.setItem(`student_name_${sessionCode}`, name);
    } catch (e) {
      console.error('Erreur lors de la sauvegarde du nom:', e);
    }
    
    // Connecter l'étudiant à la session
    await connectToSession(name);
  };

  // Se connecter automatiquement si le nom est disponible (URL ou popup)
  useEffect(() => {
    if (finalStudentName && sessionCode && session) {
      connectToSession(finalStudentName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalStudentName, sessionCode, session]);

  const handleSubmitAnswer = useCallback(async () => {
    if (!currentQuestion || answerSubmitted) return;

    try {
      const response = await ApiService.submitAnswer(
        currentQuestion.id,
        studentId,
        finalStudentName || 'Étudiant',
        selectedAnswer
      );

      if (response.success) {
        setAnswerSubmitted(true);
        // Marquer cette question comme répondue
        setAnsweredQuestions(prev => {
          const newSet = new Set(prev);
          newSet.add(currentQuestion.id);
          
          // Sauvegarder aussi dans localStorage comme backup
          const currentStudentId = `student_${sessionCode}_${finalStudentName?.toLowerCase().replace(/\s+/g, '_') || 'anonymous'}`;
          localStorage.setItem(`answered_${sessionCode}_${currentStudentId}`, JSON.stringify(Array.from(newSet)));
          
          return newSet;
        });
        
        // Attendre un court délai avant de passer à la question suivante
        setTimeout(() => {
          // Créer un Set temporaire avec la question actuelle ajoutée
          const tempAnsweredQuestions = new Set(answeredQuestions);
          tempAnsweredQuestions.add(currentQuestion.id);
          
          // Trouver la prochaine question non répondue avec le Set mis à jour
          let nextUnansweredIndex = -1;
          for (let i = 0; i < questions.length; i++) {
            if (!tempAnsweredQuestions.has(questions[i].id)) {
              nextUnansweredIndex = i;
              break;
            }
          }
          
          console.log('🔍 Recherche prochaine question non répondue:');
          console.log('📊 Questions répondues (avec actuelle):', Array.from(tempAnsweredQuestions));
          console.log('🎯 Prochaine question trouvée:', nextUnansweredIndex);
          
          if (nextUnansweredIndex !== -1) {
            // Vérifier que ce n'est pas la même question que l'actuelle
            if (nextUnansweredIndex !== currentQuestionIndex) {
              // Passer à la prochaine question non répondue
              setCurrentQuestionIndex(nextUnansweredIndex);
              setSelectedAnswer('');
              setAnswerSubmitted(false);
              setTimerExpired(false);
              console.log('✅ Navigation vers la question:', nextUnansweredIndex);
            } else {
              // Si c'est la même question, essayer la suivante
              const nextIndex = currentQuestionIndex + 1;
              if (nextIndex < questions.length) {
                setCurrentQuestionIndex(nextIndex);
                setSelectedAnswer('');
                setAnswerSubmitted(false);
                setTimerExpired(false);
                console.log('✅ Navigation vers la question suivante:', nextIndex);
              } else {
                // Toutes les questions ont été répondues
                setShowCompletionMessage(true);
                loadSession().then(() => {
                  setSessionLoadedAfterCompletion(true);
                });
                console.log('🎉 Toutes les questions répondues - affichage completion');
              }
            }
          } else {
            // Toutes les questions ont été répondues
            setShowCompletionMessage(true);
            loadSession().then(() => {
              setSessionLoadedAfterCompletion(true);
            });
            console.log('🎉 Toutes les questions répondues - affichage completion');
          }
        }, 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la soumission de la réponse');
    }
  }, [currentQuestion, answerSubmitted, studentId, finalStudentName, selectedAnswer, answeredQuestions, currentQuestionIndex, questions, sessionCode, loadSession]);

  const handleTimerExpired = useCallback(() => {
    if (!answerSubmitted && currentQuestion) {
      // Soumettre automatiquement une réponse vide ou une réponse par défaut
      setSelectedAnswer('[Pas de réponse - Temps écoulé]');
      handleSubmitAnswer();
    }
  }, [answerSubmitted, currentQuestion, handleSubmitAnswer]);


  if (showNameModal) {
    return (
      <div className="quiz-container">
        <div className="name-modal-overlay">
          <div className="name-modal">
            <h2>👋 Bonjour !</h2>
            <p>Veuillez entrer votre nom pour commencer le quiz</p>
            {session ? (
              <div className="session-preview">
                <h3 className="session-preview-title">{session.name}</h3>
                {session.description ? (
                  <div
                    className="session-preview-description"
                    dangerouslySetInnerHTML={{
                      __html: marked.parse(session.description, {
                        breaks: true,
                        gfm: true
                      }) as string
                    }}
                  />
                ) : (
                  <p className="session-preview-placeholder">
                    Aucune description n'est disponible pour cette session.
                  </p>
                )}
              </div>
            ) : (
              <div className="session-preview session-preview-loading">
                <p>Chargement des informations de la session…</p>
              </div>
            )}

            <form onSubmit={handleNameSubmit}>
              <input
                type="text"
                value={inputName}
                onChange={(e) => setInputName(e.target.value)}
                placeholder="Votre nom"
                className="name-input"
                autoFocus
              />
              <button type="submit" className="submit-name-btn">
                Commencer
              </button>
            </form>
            {error && <p className="error-message">{error}</p>}
          </div>
        </div>
        <style>{`
          .name-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }

          .session-preview {
            background: #f5f8fa;
            border: 1px solid #e1e8ed;
            border-radius: 10px;
            padding: 20px;
            text-align: left;
            margin-bottom: 25px;
          }

          .session-preview-title {
            margin: 0 0 10px 0;
            font-size: 1.2em;
            color: #2c3e50;
          }

          .session-preview-description {
            color: #4a5568;
            line-height: 1.6;
            font-size: 0.95em;
          }

          .session-preview-description p {
            margin: 0 0 0.75rem 0;
          }

          .session-preview-description ul {
            margin: 0.5rem 0 0.5rem 1.2rem;
          }

          .session-preview-description li {
            margin: 0.25rem 0;
          }

          .session-preview-placeholder {
            margin: 0;
            color: #95a5a6;
            font-style: italic;
            font-size: 0.95em;
          }

          .session-preview-loading {
            background: rgba(243, 246, 249, 0.8);
            border: 1px dashed #d0d7de;
            border-radius: 10px;
            padding: 20px;
            color: #95a5a6;
            font-style: italic;
            margin-bottom: 25px;
          }

          .name-modal {
            background: white;
            padding: 40px;
            border-radius: 15px;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
            max-width: 400px;
            width: 90%;
            text-align: center;
          }

          .name-modal h2 {
            margin: 0 0 10px 0;
            color: #2c3e50;
            font-size: 1.8em;
          }

          .name-modal p {
            margin: 0 0 25px 0;
            color: #7f8c8d;
          }

          .name-input {
            width: 100%;
            padding: 15px;
            border: 2px solid #ecf0f1;
            border-radius: 8px;
            font-size: 1.1em;
            margin-bottom: 15px;
            box-sizing: border-box;
          }

          .name-input:focus {
            outline: none;
            border-color: #3498db;
          }

          .submit-name-btn {
            width: 100%;
            padding: 15px;
            background: #3498db;
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 1.1em;
            font-weight: bold;
            cursor: pointer;
            transition: background 0.2s;
          }

          .submit-name-btn:hover {
            background: #2980b9;
          }

          .error-message {
            color: #e74c3c;
            margin-top: 10px;
            font-size: 0.9em;
          }
        `}</style>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="app-container">
        <header className="header">
          <div className="header-content">
            <h1 className="logo">Quiz Autonome</h1>
          </div>
        </header>
        <main className="main-content">
          <div className="loading">
            <div className="spinner"></div>
            <p className="mt-2">Chargement de la session...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-container">
        <header className="header">
          <div className="header-content">
            <h1 className="logo">Quiz Autonome</h1>
          </div>
        </header>
        <main className="main-content">
          <div className="card">
            <div className="card-content">
          <h2>Erreur</h2>
          <p>{error}</p>
              <button className="btn btn-primary" onClick={() => navigate('/')}>Retour à l'accueil</button>
        </div>
          </div>
        </main>
      </div>
    );
  }

  if (showCompletionMessage) {
    return (
      <div className="app-container">
        <header className="header">
          <div className="header-content">
            <h1 className="logo">Quiz Autonome</h1>
        </div>
        </header>
        <main className="main-content">
          {!showStatistics ? (
            <>
              <div className="card text-center">
                <div className="card-content">
                  <div className="completion-animation">
                    <div className="success-icon">✅</div>
                    <div className="celebration">🎉</div>
      </div>
                  <h2>Félicitations !</h2>
                  <h3>Quiz terminé avec succès</h3>
                  <div className="completion-stats">
                    <div className="stat-item">
                      <span className="stat-number">{questions.length}</span>
                      <span className="stat-label">Questions répondues</span>
        </div>
                    <div className="stat-item">
                      <span className="stat-number">100%</span>
                      <span className="stat-label">Taux de completion</span>
      </div>
                    <div className="stat-item" style={{
                      background: getCurrentScore() >= 80 ? '#d4edda' : getCurrentScore() >= 60 ? '#fff3cd' : '#f8d7da',
                      border: getCurrentScore() >= 80 ? '1px solid #c3e6cb' : getCurrentScore() >= 60 ? '1px solid #ffeaa7' : '1px solid #f5c6cb'
                    }}>
                      <span className="stat-number" style={{
                        color: getCurrentScore() >= 80 ? '#155724' : getCurrentScore() >= 60 ? '#856404' : '#721c24'
                      }}>{getCurrentScore()}%</span>
                      <span className="stat-label" style={{
                        color: getCurrentScore() >= 80 ? '#155724' : getCurrentScore() >= 60 ? '#856404' : '#721c24'
                      }}>Score moyen</span>
        </div>
          </div>
                  <p className="completion-description">
                    Vous avez brillamment terminé ce quiz autonome. 
                    Vos réponses ont été enregistrées et seront analysées par votre enseignant.
                  </p>
                  {getCurrentScore() >= 80 && (
                    <div className="score-message" style={{
                      margin: '20px 0',
                      padding: '15px',
                      background: '#d4edda',
                      border: '1px solid #c3e6cb',
                      borderRadius: '8px',
                      color: '#155724',
                      textAlign: 'center',
                      fontWeight: 'bold'
                    }}>
                      🎉 Excellent travail ! Votre score de {getCurrentScore()}% montre une très bonne compréhension du sujet.
        </div>
                  )}
                  {getCurrentScore() >= 60 && getCurrentScore() < 80 && (
                    <div className="score-message" style={{
                      margin: '20px 0',
                      padding: '15px',
                      background: '#fff3cd',
                      border: '1px solid #ffeaa7',
                      borderRadius: '8px',
                      color: '#856404',
                      textAlign: 'center',
                      fontWeight: 'bold'
                    }}>
                      👍 Bon travail ! Votre score de {getCurrentScore()}% montre une bonne compréhension. Continuez à vous améliorer !
      </div>
                  )}
                  {getCurrentScore() < 60 && getCurrentScore() > 0 && (
                    <div className="score-message" style={{
                      margin: '20px 0',
                      padding: '15px',
                      background: '#f8d7da',
                      border: '1px solid #f5c6cb',
                      borderRadius: '8px',
                      color: '#721c24',
                      textAlign: 'center',
                      fontWeight: 'bold'
                    }}>
                      💪 Votre score de {getCurrentScore()}% montre qu'il y a encore des progrès à faire. N'hésitez pas à revoir les concepts !
        </div>
                  )}
                  {!scoreCalculated && (
                    <div className="score-message" style={{
                      margin: '20px 0',
                      padding: '15px',
                      background: '#e3f2fd',
                      border: '1px solid #bbdefb',
                      borderRadius: '8px',
                      color: '#1976d2',
                      textAlign: 'center',
                      fontWeight: 'bold'
                    }}>
                      📊 Calcul du score en cours...
        </div>
                  )}
                  <div className="completion-actions">
          <button
                      className="btn btn-primary"
                      onClick={() => navigate('/')}
          >
                      🏠 Retour à l'accueil
          </button>
                    <button 
                      className="btn btn-info"
                      onClick={async () => {
                        setShowStatistics(true);
                        if (!sessionLoadedAfterCompletion) {
                          await loadSession();
                          setSessionLoadedAfterCompletion(true);
                        }
                      }}
                    >
                      📊 Voir mes statistiques
          </button>
        </div>
      </div>
        </div>
      <style>{`
                .completion-animation {
                  margin-bottom: 30px;
                }

                .success-icon {
                  font-size: 4em;
                  animation: bounce 1s ease-in-out;
                  display: inline-block;
                }

                .celebration {
                  font-size: 2em;
                  margin-left: 10px;
                  animation: rotate 2s linear infinite;
          }

          @keyframes bounce {
                  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
                  40% { transform: translateY(-20px); }
                  60% { transform: translateY(-10px); }
                }

                @keyframes rotate {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }

                .completion-stats {
                  display: flex;
                  justify-content: center;
                  gap: 30px;
                  margin: 30px 0;
                  flex-wrap: wrap;
                }

                .stat-item {
          text-align: center;
                  background: #f8f9fa;
                  padding: 20px;
                  border-radius: 8px;
                  border: 1px solid #e9ecef;
                  min-width: 120px;
                }

                .stat-number {
                  display: block;
                  font-size: 2em;
                  font-weight: bold;
                  color: #3498db;
                  margin-bottom: 5px;
                }

                .stat-label {
                  display: block;
                  font-size: 0.9em;
                  color: #7f8c8d;
                }

                .completion-description {
            font-size: 1.1em;
                  line-height: 1.6;
                  margin: 30px 0;
          color: #2c3e50;
                  max-width: 500px;
                  margin-left: auto;
                  margin-right: auto;
                }

                .completion-actions {
          display: flex;
          gap: 15px;
                  justify-content: center;
                  flex-wrap: wrap;
                  margin-top: 40px;
          }
        `}</style>
            </>
          ) : (
            <>
              <div className="card">
                <div className="card-header">
                  <h2 className="card-title">📊 Mes Statistiques</h2>
                  <button 
                    className="btn btn-outline btn-sm"
                    onClick={() => setShowStatistics(false)}
                  >
                    ← Retour au message de completion
                  </button>
      </div>
                <div className="card-content">
                  <StudentSessionStats 
                    session={session}
                    questions={questions}
                    studentId={`student_${sessionCode}_${finalStudentName?.toLowerCase().replace(/\s+/g, '_') || 'anonymous'}`}
                    studentName={finalStudentName || 'Étudiant'}
                  />
        </div>
          </div>
            </>
          )}
        </main>
        </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className="app-container">
        <header className="header">
          <div className="header-content">
            <h1 className="logo">Quiz Autonome</h1>
            <div className="header-actions">
          <button
                onClick={() => navigate('/')} 
                className="btn btn-outline btn-sm"
          >
                ← Retour
          </button>
        </div>
      </div>
        </header>
        <main className="main-content">
          <div className="card text-center">
            <div className="card-content">
              <div className="empty-state-icon">📝</div>
              <h2>Aucune question disponible</h2>
              <p>Il semble qu'il n'y ait pas de questions dans cette session pour le moment.</p>
              <p className="empty-state-subtitle">Veuillez contacter votre enseignant pour plus d'informations.</p>
              <button onClick={() => navigate('/')} className="btn btn-primary">
                Retour à l'accueil
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // const progress = ((currentQuestionIndex + 1) / questions.length) * 100; // Non utilisé pour le moment

  // Fonction pour rendre le contenu de la question selon son type
  const renderQuestionContent = () => {
    if (!currentQuestion) return null;

    switch (currentQuestion.type) {
      case 'multiple_choice':
      case 'investigation':
      case 'true_false':
        return (
          <div>
            <h4>Choisissez votre réponse:</h4>
            <div className="mt-2">
              {currentQuestion.options?.map((option: any) => {
                const isSelected = selectedAnswer === option.id;
                const isCorrectAnswer = currentQuestion.correctAnswer === option.id;
                const isSubmitted = answerSubmitted;
                
                // Style de base
                let optionStyle: any = {
                  cursor: answerSubmitted ? 'not-allowed' : 'pointer',
                  borderRadius: '8px',
                  opacity: answerSubmitted ? 0.6 : 1,
                  transition: 'all 0.3s ease',
                  marginBottom: '0.75rem',
                  padding: '1rem',
                  position: 'relative'
                };

                // Style selon l'état
                if (isSubmitted && currentQuestion.correctAnswer && currentQuestion.type !== 'investigation') {
                  if (isSelected && isCorrectAnswer) {
                    // Réponse correcte sélectionnée
                    optionStyle = {
                      ...optionStyle,
                      background: '#d4edda',
                      border: '2px solid #28a745',
                      color: '#155724'
                    };
                  } else if (isSelected && !isCorrectAnswer) {
                    // Mauvaise réponse sélectionnée
                    optionStyle = {
                      ...optionStyle,
                      background: '#f8d7da',
                      border: '2px solid #dc3545',
                      color: '#721c24'
                    };
                  } else if (!isSelected && isCorrectAnswer) {
                    // Bonne réponse non sélectionnée
                    optionStyle = {
                      ...optionStyle,
                      background: '#d1ecf1',
                      border: '2px solid #17a2b8',
                      color: '#0c5460'
                    };
                  } else {
                    // Autres options
                    optionStyle = {
                      ...optionStyle,
                      background: '#f8f9fa',
                      border: '1px solid #dee2e6',
                      color: '#6c757d'
                    };
                  }
                } else {
                  // Style normal (non soumis)
                  optionStyle = {
                    ...optionStyle,
                    background: isSelected ? '#e3f2fd' : '#ffffff',
                    border: isSelected ? '2px solid #2196f3' : '1px solid #dee2e6',
                    color: isSelected ? '#1976d2' : '#212529'
                  };
                }

                return (
                  <div 
                    key={option.id} 
                    style={optionStyle}
                    onClick={() => {
                      if (!answerSubmitted) {
                        setSelectedAnswer(option.id);
                      }
                    }}
                  >
                    <label style={{ cursor: 'inherit', margin: 0, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <input
                        type="radio"
                        name="answer"
                        value={option.id}
                        checked={isSelected}
                        onChange={(e) => setSelectedAnswer(e.target.value)}
                        disabled={answerSubmitted}
                        style={{ margin: 0 }}
                      />
                      <span style={{ fontWeight: isSelected ? 'bold' : 'normal' }}>{option.text}</span>
                      {isSubmitted && isCorrectAnswer && (
                        <span style={{ marginLeft: 'auto', fontSize: '1.2em' }}>✅</span>
                      )}
                      {isSubmitted && isSelected && !isCorrectAnswer && (
                        <span style={{ marginLeft: 'auto', fontSize: '1.2em' }}>❌</span>
                      )}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'open_ended':
        return (
          <div>
            <h4>Votre réponse:</h4>
            <div className="mt-2">
              {answerSubmitted ? (
                <div className="answer-display" style={{
                  padding: '1rem',
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }}>
                  <strong>Votre réponse :</strong>
                  <p style={{ margin: '0.5rem 0 0 0', fontStyle: 'italic' }}>
                    {selectedAnswer}
                  </p>
                </div>
              ) : (
                <textarea
                  value={selectedAnswer}
                  onChange={(e) => setSelectedAnswer(e.target.value)}
                  placeholder="Tapez votre réponse ici..."
                  disabled={answerSubmitted || timerExpired}
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '15px',
                    border: '2px solid #ecf0f1',
                    borderRadius: '8px',
                    fontSize: '1em',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              )}
              {/* {currentQuestion.proposition && (
                <div className="proposition-hint" style={{
                  marginTop: '15px',
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '0.9em',
                  color: '#6c757d'
                }}>
                  <strong>💡 Proposition de réponse :</strong>
                  <div dangerouslySetInnerHTML={{ __html: marked(currentQuestion.proposition).toString() }} />
                </div>
              )} */}
            </div>
          </div>
        );

      case 'rating':
        const min = currentQuestion.ratingScale?.min || 1;
        const max = currentQuestion.ratingScale?.max || 5;
        const labels = currentQuestion.ratingScale?.labels || [];
        
        return (
          <div>
            <h4>Donnez votre évaluation:</h4>
            <div className="mt-2">
              {answerSubmitted ? (
                <div className="rating-display">
                  <span className="rating-badge" style={{
                    padding: '0.5rem 1rem',
                    background: '#d4edda',
                    color: '#155724',
                    border: '2px solid #c3e6cb',
                    borderRadius: '8px',
                    fontWeight: 'bold'
                  }}>
                    {labels[parseInt(selectedAnswer) - min] || selectedAnswer}
                  </span>
                  {/* Affichage de l'explication pour les questions rating si disponible */}
                  {labels[parseInt(selectedAnswer) - min] && (
                    <div 
                      className="explication-display"
                      style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        background: 'rgba(33, 150, 243, 0.1)',
                        borderRadius: '8px',
                        borderLeft: '4px solid #2196f3',
                        fontSize: '0.9rem',
                        lineHeight: '1.5',
                        color: '#1976d2'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <i className="fas fa-info-circle" style={{ color: '#2196f3' }}></i>
                        <strong>Note :</strong>
                      </div>
                      <p style={{ margin: 0 }}>
                        Vous avez donné une note de {selectedAnswer} sur {max}.
                        {labels[parseInt(selectedAnswer) - min] && 
                         ` Cela correspond à : "${labels[parseInt(selectedAnswer) - min]}".`}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rating-scale" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {Array.from({ length: max - min + 1 }, (_, i) => {
                    const value = min + i;
                    const isSelected = selectedAnswer === value.toString();
                    
                    return (
                      <div 
                        key={value} 
                        style={{
                          cursor: 'pointer',
                          padding: '0.75rem 1rem',
                          borderRadius: '8px',
                          border: isSelected ? '2px solid #2196f3' : '1px solid #dee2e6',
                          background: isSelected ? '#e3f2fd' : '#ffffff',
                          color: isSelected ? '#1976d2' : '#212529',
                          transition: 'all 0.3s ease',
                          textAlign: 'center',
                          minWidth: '60px'
                        }}
                        onClick={() => setSelectedAnswer(value.toString())}
                      >
                        <input
                          type="radio"
                          name="rating"
                          value={value}
                          checked={isSelected}
                          onChange={(e) => setSelectedAnswer(e.target.value)}
                          style={{ margin: 0, marginRight: '0.5rem' }}
                        />
                        <span style={{ fontWeight: 'bold' }}>{value}</span>
                        {labels[value - min] && (
                          <div style={{ fontSize: '0.8em', marginTop: '0.25rem' }}>
                            {labels[value - min]}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );

      default:
        return <div>Type de question non supporté</div>;
    }
  };

  return (
    <div className="app-container">
      <header className="header">
        <div className="header-content">
          <h1 className="logo">{session?.name || 'Quiz Autonome'}</h1>
          <div className="header-actions">
            <button 
              onClick={() => navigate('/')} 
              className="btn btn-outline btn-sm"
            >
              ← Retour
            </button>
            <div className="status-badge status-active">
              🔄 Mode Autonome
            </div>
            <span className="text-white">| {finalStudentName || 'Étudiant'}</span>
          </div>
        </div>
      </header>

      <main className="main-content">
        {error && (
          <div className="mb-3 p-2" style={{ 
            background: '#fff3cd', 
            color: '#856404', 
            borderRadius: '4px',
            border: '1px solid #ffeaa7'
          }}>
            ⚠️ {error}
          </div>
        )}

        <div className="card fade-in">
          <div className="card-header">
            <h2 className="card-title">{currentQuestion.title}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span className="status-badge status-active">
                {getQuestionTypeDisplay(currentQuestion.type)}
              </span>
              {!session.disableTimer && (
                <TimerWrapper
                  timeLimit={currentQuestion.timeLimit || 60}
                  onTimeUp={handleTimerExpired}
                  isActive={!answerSubmitted && !timerExpired}
                  questionId={currentQuestion.id}
                />
              )}
            </div>
          </div>

          <div className="card-content">
            <div className="mb-3">
              <h4>Question:</h4>
              <div dangerouslySetInnerHTML={{ __html: marked(currentQuestion.content || '').toString() }} />
            </div>

            <div className="question-answer">
              {renderQuestionContent()}
              
              {/* Affichage des explications après soumission pour les questions à choix multiple, investigation et vrai/faux */}
              {answerSubmitted && (currentQuestion.type === 'multiple_choice' || currentQuestion.type === 'true_false') && currentQuestion.options && (
                <div className="mt-3">
                  {currentQuestion.options.map((option: any, index: number) => {
                    // Pour les questions vrai/faux, on compare avec l'index
                    const isSelected = currentQuestion.type === 'true_false' 
                      ? (selectedAnswer === 'true' && index === 0) || (selectedAnswer === 'false' && index === 1)
                      : selectedAnswer === option.id;
                    const isCorrectAnswer = currentQuestion.correctAnswer === option.id;
                    
                    if (isSelected && option.explication) {
                      return (
                        <div 
                          key={option.id}
                          className="explication-display"
                          style={{
                            marginTop: '1rem',
                            padding: '1rem',
                            background: isCorrectAnswer ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)',
                            borderRadius: '8px',
                            borderLeft: `4px solid ${isCorrectAnswer ? '#28a745' : '#dc3545'}`,
                            fontSize: '0.9rem',
                            lineHeight: '1.5',
                            color: isCorrectAnswer ? '#155724' : '#721c24'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                            <i className={`fas ${isCorrectAnswer ? 'fa-check-circle' : 'fa-times-circle'}`} 
                               style={{ color: isCorrectAnswer ? '#28a745' : '#dc3545' }}></i>
                            <strong>Explication :</strong>
                          </div>
                          <p style={{ margin: 0 }}
                            dangerouslySetInnerHTML={{ __html: marked.parse(option.explication, {
                              breaks: true,
                              gfm: true
                            }) as string }}/>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              )}
              
              {/* Message de confirmation après soumission */}
              {answerSubmitted && (
                <div className="submission-message" style={{
                  marginTop: '20px',
                  padding: '15px',
                  backgroundColor: '#d4edda',
                  border: '1px solid #c3e6cb',
                  borderRadius: '8px',
                  color: '#155724',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  ✅ Réponse soumise avec succès ! Passage à la question suivante...
                </div>
              )}
            </div>

            <div className="question-actions">
              <button
                className="btn btn-primary"
                onClick={handleSubmitAnswer}
                disabled={!selectedAnswer || answerSubmitted || timerExpired}
              >
                {answerSubmitted ? '✓ Réponse enregistrée' : 'Soumettre la réponse'}
              </button>
              {answerSubmitted && (
                <p className="success-message">✓ Réponse soumise avec succès !</p>
              )}
            </div>
          </div>
        </div>

        <div className="card mt-4">
          <div className="card-header">
            <h3 className="card-title">📊 Informations de session</h3>
          </div>
          <div className="card-content">
            <div className="grid grid-3">
              <div>
                <strong>Session:</strong><br />
                {session?.name || 'N/A'}
              </div>
              <div>
                <strong>Code:</strong><br />
                <code>{sessionCode || 'N/A'}</code>
              </div>
              <div>
                <strong>Progression:</strong><br />
                <span className="status-badge status-active">
                  {answeredQuestions.size}/{questions.length} répondues
                </span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default StudentAutonomousQuiz;
